// Narrowing a store list that is now thousands of rows long.
//
// `GET /organizations/:id/locations` is unpaginated by design (docs/API.md §4.2), so the
// whole list is already in memory and this filters it there — no request per keystroke,
// no spinner, no stale-response race. That is only reasonable because the set is scoped
// to one organization; the flat searches elsewhere are server-side for the same reason
// in reverse.
//
// IT HAD TO BE WRITTEN THE DAY THE STORES ARRIVED. Before the cutover import an
// organization had a handful of locations and a scroll list was fine. After it, Marco's
// alone carries over a thousand, and "scroll until you see it" stops being a way to find
// a store while a customer is on the phone.

// Every field an agent might have in front of them. A store's `name` on this desk already
// carries the street and the city — "MMP 110 - 2410 Geer Rd, Turlock, CA" — so the four
// things people actually search by (store number, street number, street, town) are all in
// it. `address`, `account_number` and `phone` are joined in anyway: they cost nothing, and
// a desk that renames its stores should not silently lose the ability to search them.
function haystack(row) {
  return [row?.name, row?.address, row?.account_number, row?.phone]
    .filter((part) => part !== null && part !== undefined && part !== '')
    .join(' ')
    .toLowerCase();
}

/**
 * Split on whitespace, so every token must appear somewhere in the row.
 *
 * AND rather than OR, and substrings rather than whole words. "geer turlock" finds the
 * Turlock store on Geer Rd even though no single field contains that phrase, and "mmp 110"
 * is not defeated by the hyphen in "MMP 110 - 2410". An OR would return most of the list
 * for a two-word query and read as though the filter were broken.
 */
export function searchTokens(query) {
  return String(query ?? '').toLowerCase().split(/\s+/).filter(Boolean);
}

/**
 * The rows to show for `query`, in the order they arrived (the API sorts by name).
 *
 * An empty or whitespace-only query returns EVERY row rather than none: the field starts
 * empty, and a list that renders nothing until you type looks broken and hides the one
 * store an organization may have.
 */
export function filterStores(rows, query) {
  const list = Array.isArray(rows) ? rows : [];
  const tokens = searchTokens(query);
  if (tokens.length === 0) return list;
  return list.filter((row) => {
    const text = haystack(row);
    return tokens.every((token) => text.includes(token));
  });
}

// How many rows to put in the DOM at once. A thousand focusable buttons is slow to render
// and useless to scroll; this keeps the list responsive and `storeCountLabel` tells the
// agent the rest are there rather than letting them believe they have seen everything.
export const MAX_VISIBLE_STORES = 50;

/**
 * What to say under the list, or null when there is nothing worth saying.
 *
 * Three cases, and the middle one is the reason this exists: a truncated list that says
 * nothing teaches an agent that the store they want is absent, and they stop looking.
 */
export function storeCountLabel(total, shown, query) {
  if (total === 0) {
    return searchTokens(query).length > 0 ? 'No stores match that.' : null;
  }
  if (shown < total) return `Showing ${shown} of ${total}. Keep typing to narrow.`;
  if (searchTokens(query).length > 0) {
    return total === 1 ? '1 store' : `${total} stores`;
  }
  return null;
}
